import { html, nothing, PropertyValues, TemplateResult } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { classMap } from "lit/directives/class-map.js";
import { keyed } from "lit/directives/keyed.js";
import { DodonaElement } from "@/components/meta/dodona_element";
import { I18n } from "@/i18n/i18n";
import { judgingMessages, judgingTips } from "@/components/judging_messages";
import { type HandinPhase, type HandinProgress, IDLE, inProgress } from "@/handin_progress";

type JudgingStep = "submitted" | "queued" | "running" | "processing";

const MESSAGE_ROTATE_MS = 3_000;

const PHASE_STEPS: Record<HandinPhase, JudgingStep | null> = {
    idle: null,
    submitting: "submitted",
    queued: "queued",
    running: "running",
};

const STEPS: Array<{ id: JudgingStep; icon: string }> = [
    { id: "submitted", icon: "mdi-cloud-upload" },
    { id: "queued", icon: "mdi-format-list-bulleted" },
    { id: "running", icon: "mdi-flask-outline" },
    { id: "processing", icon: "mdi-message-text-outline" },
];

/**
 * A full-card overlay shown on top of the code editor while a submission is being judged.
 * Displays a 4-step progress stepper (Submitted → Queued → Running tests → Processing feedback)
 * and rotating microcopy (whimsical messages, with the second slot always a tip) that
 * is replaced by a patience message while judging is slow.
 * The active step follows the phase in `progress`, which d-handin-card sets. While a hand-in is in
 * progress, the element has the `active` attribute, which handin.css.scss uses to show it.
 * It is visual only: d-handin-announcer announces the progress to screen readers.
 *
 * Styles: app/assets/stylesheets/components/judging_overlay.css.scss
 *
 * @element d-judging-overlay
 *
 * @prop {HandinProgress} progress - Where the hand-in is. d-handin-card sets it.
 */
@customElement("d-judging-overlay")
export class JudgingOverlay extends DodonaElement {
    @property({ attribute: false })
    progress: HandinProgress = IDLE;

    @state() private message = "";
    @state() private messageKey = 0;

    private messageIndex = 0;

    private rotateTimer: ReturnType<typeof setInterval> | null = null;

    willUpdate(changed: PropertyValues): void {
        if (!changed.has("progress")) return;
        const previous = (changed.get("progress") as HandinProgress | undefined) ?? IDLE;
        const judging = inProgress(this.progress);
        this.toggleAttribute("active", judging);

        if (judging && !inProgress(previous)) {
            this.message = this.nextMessage();
            this.messageKey++;
            this.rotateTimer = setInterval(() => {
                this.message = this.nextMessage();
                this.messageKey++;
            }, MESSAGE_ROTATE_MS);
        } else if (!judging && inProgress(previous)) {
            this.clearRotateTimer();
            this.message = "";
            this.messageKey = 0;
            this.messageIndex = 0;
        }

        if (this.progress.slow && !previous.slow) {
            this.clearRotateTimer();
            this.messageKey++;
        }
    }

    disconnectedCallback(): void {
        super.disconnectedCallback();
        this.clearRotateTimer();
    }

    private clearRotateTimer(): void {
        if (this.rotateTimer !== null) {
            clearInterval(this.rotateTimer);
            this.rotateTimer = null;
        }
    }

    // i18n-tasks-use t('js.judging.step_queued') t('js.judging.step_running') t('js.judging.step_processing') t('js.judging.step_submitted') t('js.judging.step_submitting')
    // The "submitted" step reads as "Submitting" while it is the active step, and "Submitted" once past.
    private stepLabelKey(id: JudgingStep, active: boolean): string {
        return `js.judging.step_${id === "submitted" && active ? "submitting" : id}`;
    }

    private nextMessage(): string {
        // The second message shown is always a tip; all others are whimsical.
        const useTips = this.messageIndex === 1;
        this.messageIndex++;

        const locale = I18n.locale();
        const pool = useTips ? judgingTips(locale) : judgingMessages(locale);
        // Fall back to the whimsical pool when no tips are configured for this locale.
        const effective = pool.length > 0 ? pool : judgingMessages(locale);
        if (effective.length === 0) return "";

        const candidates = effective.length > 1 ? effective.filter((m) => m !== this.message) : effective;
        return candidates[Math.floor(Math.random() * candidates.length)];
    }

    private stepState(index: number, activeIndex: number): "past" | "active" | "future" {
        if (activeIndex === -1) return "future";
        if (index < activeIndex) return "past";
        if (index === activeIndex) return "active";
        return "future";
    }

    private renderStep(step: { id: JudgingStep; icon: string }, index: number, activeIndex: number): TemplateResult {
        const state = this.stepState(index, activeIndex);
        return html`
            <div class="judging-step judging-step--${state}">
                <div class="judging-step-circle">
                    <i class="mdi ${state === "past" ? "mdi-check" : step.icon}" aria-hidden="true"></i>
                </div>
                <span class="judging-step-label">${I18n.t(this.stepLabelKey(step.id, state === "active"))}</span>
            </div>
        `;
    }

    private renderConnector(afterIndex: number, activeIndex: number): TemplateResult {
        const connectorState = this.stepState(afterIndex, activeIndex);
        return html`<div class="judging-connector judging-connector--${connectorState}"></div>`;
    }

    render(): TemplateResult {
        const activeIndex = STEPS.findIndex((s) => s.id === PHASE_STEPS[this.progress.phase]);

        return html`
            <div class="judging-overlay-card" aria-hidden="true">
                <h2 class="judging-overlay-title">${I18n.t("js.judging.title")}</h2>
                <p class="judging-overlay-subtitle">${I18n.t("js.judging.subtitle")}</p>

                <div class="judging-stepper">
                    ${STEPS.map(
                        (step, i) => html`
                            ${this.renderStep(step, i, activeIndex)}
                            ${i < STEPS.length - 1 ? this.renderConnector(i, activeIndex) : nothing}
                        `,
                    )}
                </div>

                ${this.renderMicrocopy()}
            </div>
        `;
    }

    private renderMicrocopy(): TemplateResult {
        const slow = this.progress.slow;
        const microcopy = slow ? I18n.t("js.judging.slow_message") : this.message;

        return html`
            <div class="judging-overlay-microcopy-wrapper">
                ${
                    microcopy
                        ? keyed(
                              this.messageKey,
                              html`
                                  <p
                                      class=${classMap({
                                          "judging-overlay-microcopy": true,
                                          "judging-overlay-microcopy--slow": slow,
                                      })}
                                  >
                                      ${slow ? html`<i class="mdi mdi-heart-outline" aria-hidden="true"></i>` : nothing}
                                      ${microcopy}
                                  </p>
                              `,
                          )
                        : nothing
                }
            </div>
        `;
    }
}
